import type { ReactNode } from 'react';
import { sx } from '../../ui/designStyles';
import { CLOSE_BTN, KICKER, PRI, SEC, badge, isEst, pre, railStyle, rowBtn, type Honesty } from './lib';
import { Est } from '../../ui/buildLabels';
import type { GenAct, GenData, GenRail } from './gen';

/** The honesty label beside a figure, or nothing where this screen hides build labels. */
export type Tag = (b: Honesty | null | undefined) => Honesty | null;

const KPI_BOX = 'min-width:0;padding:5px 10px;display:flex;flex-direction:column;gap:0;border-left:1px solid rgba(34,39,46,.08);';

/** One KPI strip (design pvGen kpis, and Snapshot without the sparkline). */
export function KpiStrip({ kpis, tag }: { kpis: GenData['kpis']; tag: Tag }) {
  return (
    <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(165px,1fr));border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);')}>
      {kpis.map((k) => {
        const b = tag(k.b);
        return (
          <div key={k.k} style={sx(KPI_BOX)}>
            <span style={sx('display:flex;align-items:center;flex-wrap:wrap;gap:2px 5px;min-width:0;')}>
              <span style={sx('flex:1 1 auto;line-height:1.25;font-size:14px;font-weight:700;color:#3C454F;')}>{k.k}</span>
              {b && !isEst(b) && <span style={sx('flex:0 0 auto;' + badge(b))} data-honesty={b}>{b}</span>}
            </span>
            <span style={sx('white-space:nowrap;font-size:' + (/^[A-Za-z]/.test(k.v) ? '14px' : '17px') + ';line-height:1.25;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + k.vc + ';')}>{k.v}{isEst(b) && <Est />}</span>
            <span style={sx('font-size:14px;line-height:1.3;color:#54606C;text-wrap:pretty;')}>{k.s}</span>
          </div>
        );
      })}
    </div>
  );
}

/** A rail button: a link that acts, or a dead one that says why on hover. */
export function RailAct({ a, roReason }: { a: GenAct; roReason: string | null }) {
  const off = a.off ?? (a.write ? roReason : null);
  return (
    <a
      href="#"
      onClick={(e) => {
        pre(e);
        if (!off) a.go?.();
      }}
      aria-disabled={off ? 'true' : undefined}
      title={off ?? undefined}
      style={sx((a.primary ? PRI : SEC) + (off ? 'opacity:.55;cursor:not-allowed;' : ''))}
    >
      {a.t}
    </a>
  );
}

/** The right-hand panel for the picked row (design pvGen rail). */
export function GenRailBox({ rail, tag, narrow, open, onClose, roReason, draftSlot, emptyText }: {
  rail: GenRail | null;
  tag: Tag;
  narrow: boolean;
  open: boolean;
  onClose: () => void;
  roReason: string | null;
  draftSlot?: ReactNode;
  emptyText?: string;
}) {
  return (
    <div style={sx(railStyle(narrow, open))} data-testid="capture-rail">
      {narrow && (
        <a href="#" onClick={(e) => { pre(e); onClose(); }} style={sx(CLOSE_BTN)}>
          Close ✕
        </a>
      )}
      {!rail && <span style={sx('font-size:16px;line-height:1.4;color:#54606C;')}>{emptyText ?? 'Pick a row to see it here.'}</span>}
      {rail && (
        <>
          <div style={sx('display:flex;flex-direction:column;gap:2px;')}>
            <span style={sx(KICKER)}>{rail.kicker}</span>
            <span style={sx('font-size:17px;font-weight:800;line-height:1.2;')}>{rail.title}</span>
            <span style={sx('font-size:14px;color:#54606C;')}>{rail.sub}</span>
          </div>
          {rail.why && <span style={sx('font-size:16px;line-height:1.4;padding:8px 10px;border-radius:7px;background:' + rail.whyBg + ';')}>{rail.why}</span>}
          <div style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 12px;')}>
            {rail.stats.map((x) => {
              const b = tag(x.b);
              return (
                <div key={x.k} style={sx('display:flex;flex-direction:column;min-width:0;')}>
                  <span style={sx('display:flex;flex-wrap:wrap;gap:2px 5px;align-items:center;font-size:14px;font-weight:700;color:#3C454F;')}>
                    {x.k} {b && !isEst(b) && <span style={sx(badge(b))}>{b}</span>}
                  </span>
                  <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + x.c + ';')}>{x.v}{isEst(b) && <Est />}</span>
                </div>
              );
            })}
          </div>
          <div style={sx('display:flex;flex-direction:column;')}>
            <span style={sx('font-size:14px;font-weight:700;color:#3C454F;padding-bottom:4px;')}>{rail.listT}</span>
            {rail.list.map((b, i) => (
              <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;align-items:center;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                <span style={sx('min-width:0;display:flex;flex-direction:column;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:' + b.c + ';')}>{b.a}</span>
                  <span style={sx('font-size:14px;line-height:1.3;color:#54606C;')}>{b.b}</span>
                </span>
                {b.act && <span style={sx('height:28px;padding:0 9px;display:inline-flex;align-items:center;border-radius:6px;border:1px solid rgba(34,39,46,.2);font-size:14px;font-weight:700;white-space:nowrap;')}>{b.act}</span>}
              </div>
            ))}
          </div>
          {rail.draft != null &&
            (draftSlot ?? (
              <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>Atlas draft · a person reviews and sends</span>
                <span style={sx('font-size:14px;line-height:1.45;padding:8px 10px;border-radius:7px;border:1px dashed rgba(34,39,46,.25);background:#FFFFFF;')}>{rail.draft}</span>
              </div>
            ))}
          <div style={sx('display:flex;gap:6px;flex-wrap:wrap;margin-top:auto;')}>
            {rail.acts.map((a) => (
              <RailAct key={a.t} a={a} roReason={roReason} />
            ))}
          </div>
          <span style={sx('font-size:14px;line-height:1.35;color:#54606C;')}>{rail.foot}</span>
        </>
      )}
    </div>
  );
}

/**
 * The shared list layout (design pvGen): KPIs, chips (and the Forecast slider and chart),
 * the table and the rail. Clicking a row picks it; on a narrow window the rail floats over.
 */
export function GenTab({ d, sel, onSel, tag, narrow, railOpen, setRailOpen, roReason, draftSlot, railEmpty, label, toolbar, banner }: {
  d: GenData;
  sel: string | null;
  onSel: (id: string) => void;
  tag: Tag;
  narrow: boolean;
  railOpen: boolean;
  setRailOpen: (o: boolean) => void;
  roReason: string | null;
  draftSlot?: ReactNode;
  railEmpty?: string;
  label: string;
  /** Actions in the chip row, before the note (Send ready claims). */
  toolbar?: ReactNode;
  /** A strip above the table (claims to sign, code-level access). */
  banner?: ReactNode;
}) {
  const selId = sel ?? d.rows[0]?.id ?? null;
  const headStyle = 'flex:0 0 auto;position:sticky;top:0;z-index:2;min-width:' + d.minW + 'px;display:grid;grid-template-columns:' + d.cols + ';gap:0 10px;padding:0 10px;background:#F6F3EC;border-bottom:1px solid rgba(34,39,46,.14);';
  const rowStyle = (on: boolean) =>
    'min-width:' + d.minW + 'px;display:grid;grid-template-columns:' + d.cols + ';gap:0 10px;align-items:center;padding:7px 10px;text-decoration:none;color:inherit;border-bottom:1px solid rgba(34,39,46,.07);border-left:3px solid ' +
    (on ? '#2F5C97' : 'transparent') + ';background:' + (on ? 'rgba(47,92,151,.07)' : 'transparent') + ';';
  const c = d.chart;
  return (
    <div data-screen-label={'Capture ' + label} style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;')}>
      <KpiStrip kpis={d.kpis} tag={tag} />
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:6px;')}>
        <span style={sx('font-size:14px;color:#54606C;')}>{d.chipLabel}</span>
        {d.chips.map((n) => (
          <a key={n} href="#" onClick={(e) => { pre(e); d.onChip(n); }} style={sx(chipCss(d.chipOn(n)))} aria-pressed={d.chipOn(n)}>
            {n}
          </a>
        ))}
        {d.slider && (
          <span style={sx('display:inline-flex;align-items:center;gap:8px;margin-left:8px;font-size:14px;color:#3C454F;')}>
            <span>{d.slider.label}</span>
            <input
              type="range"
              min={d.slider.min}
              max={d.slider.max}
              step={d.slider.step}
              value={d.slider.value}
              aria-label={d.slider.label}
              onChange={(e) => d.slider!.onChange(Number(e.target.value))}
              style={sx('width:140px;accent-color:#2F5C97;')}
            />
            <b style={sx('font-variant-numeric:tabular-nums;')}>{d.slider.show}</b>
          </span>
        )}
        <span style={sx('flex:1 1 10px;')} />
        {toolbar}
        <span style={sx('font-size:14px;color:#54606C;')}>{d.note}</span>
      </div>
      {banner}
      {c && (
        <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:stretch;border:1px solid rgba(34,39,46,.12);border-radius:8px;background:#FCFAF4;padding:10px 12px;')}>
          <div style={sx('min-width:0;display:flex;flex-direction:column;gap:4px;')}>
            <div style={sx('position:relative;height:150px;')}>
              <svg width="100%" height="150" viewBox="0 0 600 150" preserveAspectRatio="none" style={sx('display:block;')} aria-hidden="true">
                <polygon points={c.band} fill="rgba(47,92,151,.12)" />
                {c.janX && <line x1={c.janX} y1="0" x2={c.janX} y2="150" stroke="#6B4A12" strokeWidth="1" strokeDasharray="3 3" />}
                <polyline points={c.lBase} fill="none" stroke="#54606C" strokeWidth="1.6" strokeDasharray="5 4" />
                <polyline points={c.lScen} fill="none" stroke="#2F5C97" strokeWidth="2.4" />
              </svg>
              <span style={sx(Y_LBL + 'top:0;')}>{c.yTop}</span>
              <span style={sx(Y_LBL + 'top:68px;')}>{c.yMid}</span>
              <span style={sx(Y_LBL + 'bottom:0;')}>{c.yBot}</span>
              {c.janPct && <span style={sx('position:absolute;top:2px;left:' + c.janPct + ';margin-left:4px;font-size:14px;font-weight:700;color:#6B4A12;')}>2027 rates start</span>}
            </div>
            <span style={sx('display:flex;justify-content:space-between;font-size:14px;color:#54606C;')}>
              <span>{c.xL}</span>
              <span>{c.xM}</span>
              <span>{c.xR}</span>
            </span>
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:6px;min-width:180px;font-size:14px;')}>
            <span style={sx('display:flex;align-items:center;gap:6px;')}><span style={sx('width:16px;border-top:2px dashed #54606C;')} />Current path</span>
            <span style={sx('display:flex;align-items:center;gap:6px;')}><span style={sx('width:16px;border-top:3px solid #2F5C97;')} />With scenarios</span>
            <span style={sx('display:flex;align-items:center;gap:6px;')}><span style={sx('width:16px;height:9px;background:rgba(47,92,151,.12);')} />Likely range</span>
            <span style={sx('margin-top:auto;display:flex;flex-direction:column;')}>
              <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>
                Next 12 months {tag(c.totalB) && <span style={sx("font-family:'IBM Plex Mono',monospace;font-size:14px;color:#6B4A12;")}>{tag(c.totalB)}</span>}
              </span>
              <span style={sx('font-size:19px;font-weight:800;font-variant-numeric:tabular-nums;color:#1F6B52;')}>{c.total}</span>
              <span style={sx('font-size:14px;color:#54606C;')}>{c.sub}</span>
            </span>
          </div>
        </div>
      )}
      <div style={sx('flex:1 1 auto;min-height:300px;display:grid;grid-template-columns:' + (narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(250px,30%)') + ';gap:12px;position:relative;')}>
        <div
          onClickCapture={(e) => {
            if (!narrow || railOpen) return;
            if ((e.target as HTMLElement).closest?.('a[data-row]')) setRailOpen(true);
          }}
          style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.12);border-radius:8px;background:#FCFAF4;overflow:auto;')}
        >
          <div style={sx(headStyle)} role="row">
            {d.heads.map(([t, al], i) => (
              <span key={i} role="columnheader" style={sx('padding:5px 0;font-size:14px;line-height:1.2;font-weight:700;color:#54606C;align-self:end;text-align:' + al + ';')}>
                {t}
              </span>
            ))}
          </div>
          <div style={sx('flex:1 0 auto;')}>
            {d.rows.map((r) => (
              <a key={r.id} href="#" data-row="1" onClick={(e) => { pre(e); onSel(r.id); }} style={sx(rowStyle(r.id === selId))} aria-current={r.id === selId ? 'true' : undefined}>
                {r.cells.map((cell, i) => {
                  const b = tag(cell.b);
                  return (
                    <span key={i} style={sx('min-width:0;display:flex;flex-direction:column;gap:2px;text-align:' + (cell.right ? 'right' : 'left') + ';align-items:' + (cell.right ? 'flex-end' : 'flex-start') + ';')}>
                      <span style={sx('font-size:16px;font-weight:' + (cell.w ?? '500') + ';font-variant-numeric:tabular-nums;color:' + (cell.c ?? '#22272E') + ';line-height:1.25;')}>{cell.t}{isEst(b) && <Est />}</span>
                      {cell.s && <span style={sx('font-size:14px;line-height:1.3;color:#54606C;')}>{cell.s}</span>}
                      {b && !isEst(b) && <span style={sx(badge(b))}>{b}</span>}
                      {cell.steps && (
                        <span style={sx('display:flex;gap:3px;')}>
                          {cell.steps.map((x) => (
                            <span key={x.t} title={x.t} style={sx(x.st)} />
                          ))}
                        </span>
                      )}
                    </span>
                  );
                })}
                <span style={sx('display:flex;justify-content:flex-end;')}>
                  <span style={sx(rowBtn(r.urgent))}>{r.btn}</span>
                </span>
              </a>
            ))}
            {!d.rows.length && d.empty && <div style={sx('padding:24px;font-size:16px;color:#54606C;')}>{d.empty}</div>}
          </div>
          <div style={sx('flex:0 0 auto;padding:6px 10px;border-top:1px solid rgba(34,39,46,.12);font-size:14px;color:#54606C;')}>{d.foot}</div>
        </div>
        <GenRailBox rail={d.rail} tag={tag} narrow={narrow} open={railOpen} onClose={() => setRailOpen(false)} roReason={roReason} draftSlot={draftSlot} emptyText={railEmpty} />
      </div>
    </div>
  );
}

const Y_LBL = 'position:absolute;left:2px;font-size:14px;font-weight:600;color:#54606C;background:#FCFAF4;padding:0 3px;font-variant-numeric:tabular-nums;';
const chipCss = (on: boolean) =>
  'height:28px;padding:0 10px;display:inline-flex;align-items:center;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(47,92,151,.14);color:#22272E;' : 'color:#3C454F;');
